import { motion } from 'framer-motion'
import { CheckCircleOutlined, TrophyOutlined } from '@ant-design/icons'

const About = () => {
  const milestones = [
    { year: '2019', title: '公司成立', description: '在北京成立，专注AI视觉技术研发' },
    { year: '2020', title: '技术突破', description: '自研深度学习算法，准确率突破99%' },
    { year: '2021', title: '市场扩展', description: '服务客户突破50家，覆盖多个行业' },
    { year: '2022', title: '荣誉认证', description: '获得国家高新技术企业认证' },
    { year: '2023', title: '规模壮大', description: '团队扩展至100+人，完成B轮融资' },
    { year: '2024', title: '国际化', description: '开拓海外市场，服务全球客户' },
  ]

  const values = [
    {
      title: '技术创新',
      description: '持续投入研发，保持技术领先',
      icon: '🚀',
    },
    {
      title: '客户至上',
      description: '以客户需求为导向，提供优质服务',
      icon: '⭐',
    },
    {
      title: '追求卓越',
      description: '精益求精，打造行业标杆',
      icon: '🎯',
    },
    {
      title: '合作共赢',
      description: '携手合作伙伴，共创美好未来',
      icon: '🤝',
    },
  ]

  const certifications = [
    '国家高新技术企业',
    'ISO 9001质量管理体系认证',
    '软件著作权20+项',
    '发明专利10+项',
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 页面头部 */}
      <section className="tech-bg py-20 pt-32">
        <div className="container-custom text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            关于我们
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            专注AI视觉技术，为企业数字化转型赋能
          </motion.p>
        </div>
      </section>

      {/* 公司介绍 */}
      <section className="py-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                引领AI视觉技术创新
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                我们是一家专注于AI视觉应用落地的高科技企业，致力于将前沿的计算机视觉技术转化为实际的商业价值。
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-4">
                团队由来自清华、北大、中科院等顶尖院校的AI专家组成，在目标检测、图像分割、OCR识别等领域拥有深厚的技术积累。
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                我们已为智能制造、安防监控、医疗健康、零售等多个行业的客户提供了专业的AI视觉解决方案，帮助企业提升效率、降低成本、优化决策。
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-primary-500 to-primary-700 rounded-2xl p-12 text-white"
            >
              <div className="grid grid-cols-2 gap-8">
                <div>
                  <div className="text-4xl font-bold mb-2">150+</div>
                  <div className="text-white/80">完成项目</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">80+</div>
                  <div className="text-white/80">合作客户</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">99.5%</div>
                  <div className="text-white/80">平均准确率</div>
                </div>
                <div>
                  <div className="text-4xl font-bold mb-2">100+</div>
                  <div className="text-white/80">团队成员</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 发展历程 */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">发展历程</h2>
            <p className="section-subtitle">砥砺前行，不断创新</p>
          </motion.div>

          <div className="relative">
            {/* 时间线 */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-primary-200" />

            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <motion.div
                  key={milestone.year}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`flex flex-col md:flex-row gap-8 items-center ${
                    index % 2 === 0 ? 'md:flex-row-reverse' : ''
                  }`}
                >
                  <div className="flex-1" />
                  
                  <div className="relative">
                    <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                      {milestone.year.slice(-2)}
                    </div>
                  </div>

                  <div className="flex-1">
                    <div className={`card p-6 ${index % 2 === 0 ? 'md:text-right' : ''}`}>
                      <div className="text-sm text-primary-600 font-semibold mb-2">
                        {milestone.year}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {milestone.title}
                      </h3>
                      <p className="text-gray-600">
                        {milestone.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 核心价值观 */}
      <section className="py-20 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">核心价值观</h2>
            <p className="section-subtitle">指引我们前进的方向</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="card p-8 text-center"
              >
                <div className="text-5xl mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 资质认证 */}
      <section className="py-20 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">资质认证</h2>
            <p className="section-subtitle">专业资质，值得信赖</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-4 bg-primary-50 p-6 rounded-lg"
              >
                <CheckCircleOutlined className="text-3xl text-primary-600" />
                <span className="text-lg font-medium text-gray-900">{cert}</span>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <TrophyOutlined className="text-6xl text-yellow-500 mb-4" />
            <p className="text-gray-600 text-lg">
              荣获"中国AI视觉应用创新企业TOP10"等多项荣誉
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default About

